import { Inject, Injectable } from '@angular/core';
import { ApiRequest } from '@app/core/backend';
import { ApiClient } from '@eng-ds/api-client';
import {
	NB_AUTH_OPTIONS,
	NB_AUTH_STRATEGIES,
	NbAuthResult,
	NbAuthService,
	NbAuthToken,
	NbTokenService
} from '@nebular/auth';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { UserService } from './user.service';
import * as _ from 'lodash';

@Injectable()
export class AuthService extends NbAuthService {
	private _showSessionExpiredModal = new Subject<void>();
	showSessionExpiredModal$: Observable<void> =
		this._showSessionExpiredModal.asObservable();

	constructor(
		private apiClient: ApiClient,
		private userService: UserService,
		protected tokenService: NbTokenService,
		@Inject(NB_AUTH_STRATEGIES) protected strategies,
		@Inject(NB_AUTH_OPTIONS) protected options = {}
	) {
		super(tokenService, strategies);
	}

	login(
		strategyName: string,
		userData?: { username: string; password: string, otp?: string },
	) {
		const newUserData = _.cloneDeep(userData);
		if (newUserData.otp) {
			newUserData.password = this.createXml(newUserData.password, newUserData.otp);
		}

		return this.authenticate(strategyName, newUserData).pipe(
			switchMap((result: NbAuthResult) => {
				if (result.isSuccess() && !result.getResponse()?.body?.PartialToken) {
					return this._fetchUser(result);
				}
				return of({ result });
			})
		);
	}

	generateOTPAndQrCode(): Observable<any> {
		return this.apiClient.request('generateOTPAndQrCode');
	}

	setMfa(EncryptedSecret: string, OTP: string): Observable<any> {
		return this.apiClient.request('setMFASecretKey', { EncryptedSecret, OTP });
	}

	changePassword(data: ApiRequest) {
		return this.apiClient.request('ACC_5b', { ...data });
	}

	isAuthenticated(): Observable<boolean> {
		return this.getToken().pipe(
			map((token: NbAuthToken) => token.isValid())
		);
	}

	hasToken(): Observable<boolean> {
		return this.getToken().pipe(
			map((token: NbAuthToken) => !!token.getValue())
		);
	}

	isTokenValid(): Observable<boolean> {
		return this.getToken().pipe(
			map((token: NbAuthToken) => token.isValid())
		);
	}

	refreshToken(strategyName: string, data?: any): Observable<NbAuthResult> {
		return this.getStrategy(strategyName)
			.refreshToken(data)
			.pipe(
				switchMap((result: NbAuthResult) => {
					return this._processResultToken(result);
				})
			);
	}

	private _processResultToken(result: NbAuthResult) {
		if (result.isSuccess() && result.getToken()) {
			return this.tokenService.set(result.getToken()).pipe(
				map(() => {
					return result;
				})
			);
		}

		return of(result);
	}

	emitApiUnauthorizedEvent() {
		this._showSessionExpiredModal.next();
	}

	private _fetchUser(result: NbAuthResult) {
		return this.userService.getUserData().pipe(
			tap(user => {
				return this.userService.saveUser(user);
			}),
			map(() => ({ result })),
			catchError(err =>
				of({
					result: new NbAuthResult(false, err?.error, '', [
						'LOGIN.ERRORS.FETCH_USER'
					])
				})
			)
		);
	}

	isAuthenticatedOrRefresh(): Observable<boolean> {
		return this.getToken().pipe(
			switchMap(token => {
				if (token.getValue() && !token.isValid()) {
					return this.refreshToken(
						token.getOwnerStrategyName(),
						token
					).pipe(
						switchMap(res => {
							if (res.isSuccess()) {
								return this.isAuthenticated();
							} else {
								return of(false);
							}
						})
					);
				} else {
					return of(token.isValid());
				}
			})
		);
	}

	clearSiteData(): Observable<any>{
		return this.apiClient.request('logout');
	}
	
	logout(strategyName: string): Observable<NbAuthResult> {
		return this.getStrategy(strategyName)
			.logout()
			.pipe(
				switchMap((result: NbAuthResult) => {
					if (result.isSuccess()) {
						this.userService.deleteUser();
						this.tokenService.clear().pipe(map(() => result));
					}
					return of(result);
				})
			);
	}

	createXml(userPassword: string, userOtp: string): string {
		const xml = `<root>
		  <password>${userPassword}</password>
		  <otp>${userOtp}</otp>
		</root>`;
		return xml;
	}
}
